import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const cache = new Map<string, { url: string; expiresAt: number }>();
const TTL = 60 * 60; // 1 hour

/**
 * Returns a signed URL for a `chat-attachments` storage path.
 * Caches per-path to avoid re-signing on every render, refreshes on expiry.
 */
export function useSignedAttachmentUrl(storagePath?: string | null): {
  url: string | null;
  isLoading: boolean;
} {
  const [url, setUrl] = useState<string | null>(() => {
    if (!storagePath) return null;
    const cached = cache.get(storagePath);
    return cached && cached.expiresAt > Date.now() ? cached.url : null;
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!storagePath) {
      setUrl(null);
      return;
    }
    const cached = cache.get(storagePath);
    if (cached && cached.expiresAt > Date.now()) {
      setUrl(cached.url);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    supabase.storage
      .from("chat-attachments")
      .createSignedUrl(storagePath, TTL)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setUrl(null);
        } else {
          cache.set(storagePath, {
            url: data.signedUrl,
            expiresAt: Date.now() + (TTL - 60) * 1000,
          });
          setUrl(data.signedUrl);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [storagePath]);

  return { url, isLoading };
}
